import {
  Document, Packer, Paragraph, TextRun, HeadingLevel,
  AlignmentType, Footer, ImageRun, BorderStyle,
  Header, Tab, TabStopPosition, TabStopType,
} from 'docx';
import { saveAs } from 'file-saver';

const SECTION_ORDER = [
  'job_title', 'about_company', 'job_description',
  'requirements', 'benefits', 'conditions',
];

const SECTION_LABELS = {
  job_title: 'Vacaturetitel',
  about_company: 'Over het bedrijf',
  job_description: 'Functieomschrijving',
  requirements: 'Functie-eisen',
  benefits: 'Wat wij bieden',
  conditions: 'Arbeidsvoorwaarden',
};

// Brand color
const TEAL = '0D9488';
const DARK = '0F172A';
const BODY = '334155';
const MUTED = '94A3B8';

/**
 * Generate a .docx file from workspace document sections.
 * Includes a HumanAIze-branded footer on every page.
 */
export async function generateVacancyDocx({ sections, title }) {
  const filled = SECTION_ORDER
    .filter(k => k !== 'job_title' && sections[k]?.content?.trim())
    .map(k => ({ key: k, label: SECTION_LABELS[k], content: sections[k].content.trim() }));

  if (filled.length === 0) return false;

  const jobTitle = sections.job_title?.content?.trim() || title || 'Vacaturetekst';

  // Build document paragraphs
  const children = [];

  // Title
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: jobTitle,
          bold: true,
          size: 36, // 18pt
          color: DARK,
          font: 'Calibri',
        }),
      ],
      spacing: { after: 200 },
      alignment: AlignmentType.LEFT,
    }),
  );

  // Teal divider line
  children.push(
    new Paragraph({
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 6, color: TEAL },
      },
      spacing: { after: 300 },
    }),
  );

  // Sections
  filled.forEach((section) => {
    // Section heading
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: section.label.toUpperCase(),
            bold: true,
            size: 20, // 10pt
            color: TEAL,
            font: 'Calibri',
            characterSpacing: 60, // letter-spacing
          }),
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 360, after: 120 },
      }),
    );

    // Content paragraphs
    const lines = section.content.split('\n');
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        children.push(new Paragraph({ spacing: { after: 80 } }));
        return;
      }

      const isBullet = /^[•\-\u2022\u2013*]\s/.test(trimmed);
      const text = isBullet ? trimmed.replace(/^[•\-\u2022\u2013*]\s*/, '') : trimmed;

      if (isBullet) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text, size: 22, color: BODY, font: 'Calibri' }),
            ],
            bullet: { level: 0 },
            spacing: { after: 60 },
          }),
        );
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text, size: 22, color: BODY, font: 'Calibri' }),
            ],
            spacing: { after: 100 },
            alignment: AlignmentType.LEFT,
          }),
        );
      }
    });
  });

  // Create footer with HumanAIze branding
  const footerParagraph = new Paragraph({
    children: [
      new TextRun({
        text: 'Gemaakt met ',
        size: 16, // 8pt
        color: MUTED,
        font: 'Calibri',
      }),
      new TextRun({
        text: 'Humanaize Matcher',
        bold: true,
        size: 16,
        color: TEAL,
        font: 'Calibri',
      }),
      new TextRun({
        text: ' , een product van HumanAIze  •  humanaize.nl',
        size: 16,
        color: MUTED,
        font: 'Calibri',
      }),
    ],
    alignment: AlignmentType.CENTER,
    border: {
      top: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
    },
    spacing: { before: 200 },
  });

  const doc = new Document({
    creator: 'Humanaize Matcher',
    title: jobTitle,
    description: 'Gegenereerd met Humanaize Matcher (een product van HumanAIze)',
    sections: [{
      properties: {
        page: {
          margin: {
            top: 1440,    // 1 inch
            bottom: 1440,
            left: 1440,
            right: 1440,
          },
        },
      },
      footers: {
        default: new Footer({
          children: [footerParagraph],
        }),
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const fn = `Vacature - ${jobTitle.replace(/[^a-zA-Z0-9\s\u00C0-\u024F-]/g, '').trim()}.docx`;
  saveAs(blob, fn);
  return true;
}
